/** Info tab — every run attribute as copyable rows, per-source runs (with
 *  processing reports), child runs, reimport links, and the raw
 *  input_options / processing_stats JSON. */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';

import type { ImportRunDetail, ImportRunSourceRun } from '@/api/importRuns';
import { JsonEditorWidget } from '@/components/JsonEditor/JsonEditorWidget';
import { SectionCard } from '@/components/ReadView';
import { useSnackbar } from '@/components/Snackbar';
import {
  act,
  utcTitle,
  ValueActions,
  type ValueAction,
} from '@/components/ValueActions/ValueActions';
import { TsValue, formatDuration } from '@/features/bgtasks/taskFormat';

import { ImportStateChip, importOriginLabel, importStatusLabel } from '../importRunFormat';
import {
  createdActions,
  groupActions,
  importIdActions,
  originActions,
  requestIdActions,
  sourceCodeActions,
  sourceUrlActions,
  statusActions,
  taskIdActions,
  tenantActions,
  type ImportRunCtx,
} from '../importRunLinks';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

function CopyRow({
  label,
  value,
  monospace = true,
  actions,
}: {
  label: string;
  value: string | null | undefined;
  monospace?: boolean;
  /** Where this value leads. Empty values never get a popover. */
  actions?: ValueAction[];
}) {
  const snackbar = useSnackbar();
  const text = value ? (
    <Typography variant="body2" component="span" sx={{ ...(monospace ? mono : {}), wordBreak: 'break-all' }}>
      {value}
    </Typography>
  ) : null;
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 32 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>
        {label}
      </Typography>
      {value ? (
        <>
          {actions?.length ? <ValueActions actions={actions}>{text}</ValueActions> : text}
          <Tooltip title={`Copy ${label.toLowerCase()}`}>
            <IconButton
              size="small"
              aria-label={`Copy ${label.toLowerCase()}`}
              onClick={() => {
                void navigator.clipboard.writeText(value);
                snackbar.show(`${label} copied.`, 'success');
              }}
            >
              <ContentCopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Typography variant="body2" color="text.disabled">—</Typography>
      )}
    </Stack>
  );
}

const POLICY_LABELS: Record<number, string> = { 1: 'Strict', 2: 'Relaxed' };
const MODE_LABELS: Record<number, string> = { 1: 'Full', 2: 'Append' };

function SourceRow({ source, ctx }: { source: ImportRunSourceRun; ctx: ImportRunCtx }) {
  const hasReport = Boolean(source.processing_report) || source.stats != null;
  const summary = (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
      <ValueActions actions={sourceCodeActions(source.code, ctx)}>
        <Box component="span" sx={mono}>{source.code}</Box>
      </ValueActions>
      <Typography variant="body2" color="text.secondary">{source.name}</Typography>
      <ValueActions chip actions={statusActions(importStatusLabel(source.state), ctx)}>
        <ImportStateChip state={source.state} />
      </ValueActions>
      {source.policy != null && (
        <Chip size="small" variant="outlined" label={POLICY_LABELS[source.policy] ?? `Policy ${source.policy}`} sx={{ height: 20, fontSize: 12 }} />
      )}
      {source.mode != null && (
        <Chip size="small" variant="outlined" label={MODE_LABELS[source.mode] ?? `Mode ${source.mode}`} sx={{ height: 20, fontSize: 12 }} />
      )}
    </Stack>
  );
  if (!hasReport) {
    return <Box sx={{ py: 1, px: 2, borderBottom: 1, borderColor: 'divider' }}>{summary}</Box>;
  }
  return (
    <Accordion disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, borderBottom: 1, borderColor: 'divider' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>{summary}</AccordionSummary>
      <AccordionDetails>
        {/* The copies live here, not in the summary: a trigger inside the
            AccordionSummary's ButtonBase would nest two controls. */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 0.5 }}>
          {source.processing_report && (
            <ValueActions actions={[act.copy(source.processing_report, 'Copy processing report')]}>
              <Typography variant="caption" color="primary">
                copy report
              </Typography>
            </ValueActions>
          )}
          {source.stats != null && (
            <ValueActions actions={[act.copy(JSON.stringify(source.stats, null, 2), 'Copy stats JSON')]}>
              <Typography variant="caption" color="primary">
                copy stats
              </Typography>
            </ValueActions>
          )}
        </Stack>
        {source.processing_report && (
          <Box
            component="pre"
            sx={{ m: 0, mb: source.stats ? 1.5 : 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, overflowX: 'auto', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}
          >
            {source.processing_report}
          </Box>
        )}
        {source.stats != null && <JsonEditorWidget initialValue={source.stats} readOnly mode="view" />}
      </AccordionDetails>
    </Accordion>
  );
}

export function InfoTab({ detail, detailPath }: { detail: ImportRunDetail; detailPath: (id: string) => string }) {
  const d = detail;
  const ctx: ImportRunCtx = { tenantId: d.top?.id ?? null };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard title="Run">
        <CopyRow label="Import ID" value={d.id} actions={importIdActions(d.id, ctx)} />
        <CopyRow label="Group ID" value={d.group} {...(d.group && { actions: groupActions(d.group, ctx) })} />
        <CopyRow
          label="Request ID"
          value={d.request_id}
          {...(d.request_id && { actions: requestIdActions(d.request_id, d.created_ts, ctx) })}
        />
        {/* Celery id — filters the import grid, never /bgtasks (other id space). */}
        <CopyRow label="Task ID" value={d.task_id} {...(d.task_id && { actions: taskIdActions(d.task_id, ctx) })} />
        <CopyRow label="Source URL" value={d.url} {...(d.url && { actions: sourceUrlActions(d.url) })} />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 32 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>Tenant</Typography>
          {d.top ? (
            <ValueActions actions={tenantActions(d.top)}>
              <Link component={RouterLink} to={`/tour-operators/${d.top.id}`} variant="body2">
                {d.top.name}
              </Link>
            </ValueActions>
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          )}
        </Stack>
        <CopyRow
          label="Status"
          value={importStatusLabel(d.state)}
          monospace={false}
          actions={statusActions(importStatusLabel(d.state), ctx)}
        />
        <CopyRow
          label="Origin"
          value={importOriginLabel(d.origin)}
          monospace={false}
          actions={originActions(importOriginLabel(d.origin), ctx)}
        />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 32 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>Created</Typography>
          <Typography variant="body2" component="span">
            <ValueActions actions={createdActions(d.created_ts, ctx)} title={utcTitle(d.created_ts)}>
              <TsValue value={d.created_ts} />
            </ValueActions>
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 32 }}>
          <Typography variant="body2" color="text.secondary" sx={{ width: 170, flexShrink: 0 }}>Processing</Typography>
          <Typography variant="body2" component="span">
            <TsValue value={d.processing_started_ts} /> → <TsValue value={d.processing_finished_ts} />
            {d.processing_duration != null && ` (${formatDuration(d.processing_duration)})`}
          </Typography>
        </Stack>
      </SectionCard>

      <SectionCard title={`Sources (${d.sources.length})`}>
        {d.sources.length === 0 ? (
          <Typography variant="body2" color="text.disabled">No source runs recorded.</Typography>
        ) : (
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '& > :last-child': { borderBottom: 0 } }}>
            {d.sources.map((s) => <SourceRow key={s.id} source={s} ctx={ctx} />)}
          </Box>
        )}
      </SectionCard>

      {d.children.length > 0 && (
        <SectionCard title={`Child imports (${d.children.length})`}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Import</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sources</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.children.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <ValueActions actions={[act.copy(c.id, 'Copy import id')]} title={c.id}>
                      <Link component={RouterLink} to={detailPath(c.id)} sx={mono}>
                        {c.id.slice(0, 8)}…
                      </Link>
                    </ValueActions>
                  </TableCell>
                  <TableCell><TsValue value={c.created_ts} /></TableCell>
                  <TableCell>
                    <ValueActions chip actions={statusActions(importStatusLabel(c.state), ctx)}>
                      <ImportStateChip state={c.state} />
                    </ValueActions>
                  </TableCell>
                  <TableCell sx={mono}>
                    {c.sources.length === 0
                      ? '—'
                      : c.sources.map((s, i) => (
                          <Box component="span" key={s.code}>
                            {i > 0 && ', '}
                            <ValueActions actions={sourceCodeActions(s.code, ctx)}>{s.code}</ValueActions>
                          </Box>
                        ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </SectionCard>
      )}

      {d.reimport.length > 0 && (
        <SectionCard title={`Reimported as (${d.reimport.length})`}>
          <Stack spacing={0.5}>
            {d.reimport.map((r) => (
              <Stack key={r.id} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <ValueActions actions={[act.copy(r.id, 'Copy import id')]} title={r.id}>
                  <Link component={RouterLink} to={detailPath(r.id)} sx={mono}>
                    {r.id.slice(0, 8)}…
                  </Link>
                </ValueActions>
                <Typography variant="body2" color="text.secondary" component="span">
                  <TsValue value={r.created_ts} />
                </Typography>
              </Stack>
            ))}
          </Stack>
        </SectionCard>
      )}

      {(d.input_options != null || d.processing_stats != null) && (
        <SectionCard title="Options & stats">
          {d.input_options != null && (
            <Box sx={{ mb: d.processing_stats != null ? 2 : 0 }}>
              <Typography variant="caption" color="text.secondary">input_options</Typography>
              <JsonEditorWidget initialValue={d.input_options} readOnly mode="view" />
            </Box>
          )}
          {d.processing_stats != null && (
            <Box>
              <Typography variant="caption" color="text.secondary">processing_stats</Typography>
              <JsonEditorWidget initialValue={d.processing_stats} readOnly mode="view" />
            </Box>
          )}
        </SectionCard>
      )}
    </Box>
  );
}
